import React, { useState, useEffect } from 'react';
import {
  Card, Button, Modal, Form, Input, Select, Table, Space, Tag, Popconfirm, message, Empty
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined, UserAddOutlined } from '@ant-design/icons';
import { getTeams, createTeam, updateTeam, deleteTeam, addPlayerToTeam, removePlayerFromTeam } from '../../api/services/teamService';
import { getPlayers } from '../../api/services/playerService';

const TeamManagement = ({ tenantId }) => {
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [teamsData, playersData] = await Promise.all([
        getTeams(),
        getPlayers()
      ]);
      setTeams(teamsData);
      setPlayers(playersData);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Fehler beim Laden der Teams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (team = null) => {
    setEditingTeam(team);
    if (team) {
      form.setFieldsValue({
        name: team.name,
        description: team.description,
        player_ids: team.players?.map(p => p.id) || []
      });
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingTeam(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const teamData = {
        name: values.name,
        description: values.description || '',
        tenant: tenantId,
        player_ids: values.player_ids || []
      };

      if (editingTeam) {
        await updateTeam(editingTeam.id, teamData);
        message.success('Team aktualisiert');
      } else {
        await createTeam(teamData);
        message.success('Team erstellt');
      }

      handleCloseModal();
      fetchData();
    } catch (error) {
      console.error('Error saving team:', error);
      message.error('Fehler beim Speichern des Teams');
    }
  };

  const handleDelete = async (teamId) => {
    try {
      await deleteTeam(teamId);
      message.success('Team gelöscht');
      fetchData();
    } catch (error) {
      console.error('Error deleting team:', error);
      message.error('Fehler beim Löschen des Teams');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <TeamOutlined />
          <strong>{text}</strong>
        </Space>
      )
    },
    {
      title: 'Spieler',
      dataIndex: 'player_names',
      key: 'player_names',
      render: (playerNames) => (
        <Space wrap size={[0, 4]}>
          {playerNames?.map((name, idx) => (
            <Tag key={idx} color="blue">{name}</Tag>
          )) || '-'}
        </Space>
      )
    },
    {
      title: 'Aktionen',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleOpenModal(record)}
          />
          <Popconfirm
            title="Team löschen?"
            description="Möchten Sie dieses Team wirklich löschen?"
            onConfirm={() => handleDelete(record.id)}
            okText="Ja"
            cancelText="Nein"
          >
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card
      title={
        <Space>
          <TeamOutlined />
          Teams verwalten
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenModal()}
        >
          Neues Team
        </Button>
      }
      style={{ marginBottom: '24px' }}
    >
      {teams.length > 0 ? (
        <Table
          dataSource={teams}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
        />
      ) : (
        <Empty
          description="Noch keine Teams erstellt"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" onClick={() => handleOpenModal()}>
            Erstes Team erstellen
          </Button>
        </Empty>
      )}

      <Modal
        title={editingTeam ? 'Team bearbeiten' : 'Neues Team erstellen'}
        open={modalVisible}
        onCancel={handleCloseModal}
        onOk={handleSubmit}
        okText={editingTeam ? 'Speichern' : 'Erstellen'}
        cancelText="Abbrechen"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Team-Name"
            rules={[{ required: true, message: 'Bitte einen Team-Namen eingeben' }]}
          >
            <Input placeholder="z.B. Die Profis" prefix={<TeamOutlined />} />
          </Form.Item>

          <Form.Item
            name="description"
            label="Beschreibung (optional)"
          >
            <Input.TextArea placeholder="Kurze Beschreibung des Teams..." rows={2} />
          </Form.Item>

          <Form.Item
            name="player_ids"
            label="Spieler"
            rules={[{ required: true, message: 'Bitte mindestens einen Spieler auswählen' }]}
          >
            <Select
              mode="multiple"
              placeholder="Spieler auswählen"
              optionFilterProp="children"
              showSearch
            >
              {players.map((player) => (
                <Select.Option key={player.id} value={player.id}>
                  {player.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default TeamManagement;
